import React, { createContext, useContext, useCallback, useState, ReactNode } from "react";
import { toast } from "react-toastify";
import {
  useCommandRelay,
  OnlineUser,
  IncomingCommand,
  SendCommandPayload,
  ConnectionStatus,
  CommandAction,
  getCommandLabel,
  getCommandNavigationUrl,
} from "hooks/useCommandRelay";
import { addCommandToast } from "./CommandToastContainer";

// ============ Context Types ============
interface CommandRelayContextType {
  status: ConnectionStatus;
  onlineUsers: OnlineUser[];
  currentUser: OnlineUser | null;
  sendCommand: (payload: SendCommandPayload) => boolean;
  connect: () => void;
  disconnect: () => void;
  isSenderOpen: boolean;
  openSender: () => void;
  closeSender: () => void;
}

const CommandRelayContext = createContext<CommandRelayContextType | null>(null);

// ============ Provider ============
interface CommandRelayProviderProps {
  children: ReactNode;
}

export function CommandRelayProvider({ children }: CommandRelayProviderProps) {
  const [isSenderOpen, setIsSenderOpen] = useState(false);

  const handleCommand = useCallback(
    (command: IncomingCommand) => {
      console.log("[CommandRelay] >>> COMMAND RECEIVED <<<", command);
      const { action, data, from } = command;
      const label = getCommandLabel(action, data);
      const navigateTo = getCommandNavigationUrl(action, data);
      
      // Get the copyable value
      const copyValue = action === "TRACK_AWB" 
        ? data.awbNo || "" 
        : action === "OPEN_PICKUP" 
          ? data.pickupId || "" 
          : data.message || "";

      // Use timestamp from server as toast ID (same across all tabs)
      const toastId = `cmd_${command.timestamp}_${from.id}`;
      
      // Add toast - this syncs to all tabs via localStorage
      addCommandToast({
        id: toastId,
        fromUser: from,
        label,
        copyValue,
        navigateTo,
        timestamp: command.timestamp,
      });
    },
    []
  );

  const handleOnlineUsersChange = useCallback((users: OnlineUser[]) => {
    console.log("[CommandRelay] Online users updated:", users.length);
  }, []);

  const handleError = useCallback((message: string) => {
    toast.error(`Command Relay Error: ${message}`, {
      position: "top-right",
      autoClose: 5000,
    });
  }, []);

  const handleSent = useCallback((action: CommandAction, targetUserId: number) => {
    toast.success(`Command sent successfully`, {
      position: "top-right",
      autoClose: 3000,
    });
  }, []);

  const {
    status,
    onlineUsers,
    currentUser,
    sendCommand,
    connect,
    disconnect,
  } = useCommandRelay({
    onCommand: handleCommand,
    onOnlineUsersChange: handleOnlineUsersChange,
    onError: handleError,
    onSent: handleSent,
  });

  const openSender = useCallback(() => setIsSenderOpen(true), []);
  const closeSender = useCallback(() => setIsSenderOpen(false), []);

  const value: CommandRelayContextType = {
    status,
    onlineUsers,
    currentUser,
    sendCommand,
    connect,
    disconnect,
    isSenderOpen,
    openSender,
    closeSender,
  };

  return (
    <CommandRelayContext.Provider value={value}>
      {children}
    </CommandRelayContext.Provider>
  );
}

// ============ Hook ============
export function useCommandRelayContext() {
  const context = useContext(CommandRelayContext);
  if (!context) {
    throw new Error(
      "useCommandRelayContext must be used within a CommandRelayProvider"
    );
  }
  return context;
}

export default CommandRelayProvider;
